import { useSuspenseQuery } from "@tanstack/react-query";
import { Save, UserPlus } from "lucide-react";
import { useState } from "react";
import { useErrorDialog } from "@/shared/components/ErrorDialog";
import { Button } from "@/shared/components/ui/button";
import { useSaveRoster } from "./api/roster.mutations";
import { usersQueryOptions } from "./api/roster.queries";
import { GenerateInviteDialog } from "./components/GenerateInviteDialog";
import { RosterTable } from "./components/RosterTable";
import type { RosterUser } from "./roster.types";
import { useRosterDrafts } from "./useRosterDrafts";

const effectiveName = (u: RosterUser) => u.displayName ?? u.username;
const byEffectiveName = (a: RosterUser, b: RosterUser) =>
  effectiveName(a).localeCompare(effectiveName(b), "sv");

// Partition by the SERVER role + name so rows don't jump while an edit is unsaved (re-sections after save).
function partitionRoster(users: RosterUser[]) {
  const applicants = users
    .filter((u) => u.permissionRole === "applicant")
    .sort(byEffectiveName);
  const members = users
    .filter((u) => u.permissionRole !== "applicant")
    .sort(byEffectiveName);
  return { applicants, members };
}

export function RosterPage() {
  const { data: users } = useSuspenseQuery(usersQueryOptions);

  const saveRoster = useSaveRoster();
  const { showApiError } = useErrorDialog();

  const [inviteOpen, setInviteOpen] = useState(false);

  const { draftFor, onDraftChange, dirtyRows, buildUpdates, clear } =
    useRosterDrafts(users);

  const onSave = () => {
    const result = buildUpdates();
    if (!result.ok) {
      showApiError(new Error(result.message), { title: "Ogiltiga ändringar" });
      return;
    }
    saveRoster.mutate(
      { updates: result.updates },
      {
        onSuccess: clear,
        onError: (err) => showApiError(err, { title: "Kunde inte spara" }),
      },
    );
  };

  const { applicants, members } = partitionRoster(users);

  const tableProps = {
    draftFor,
    onDraftChange,
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          onClick={onSave}
          loading={saveRoster.isPending}
          disabled={dirtyRows.length === 0}
        >
          <Save />
          Spara
        </Button>
        <Button variant="outline" onClick={() => setInviteOpen(true)}>
          <UserPlus />
          Skapa inbjudan
        </Button>
      </div>

      <GenerateInviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />

      {applicants.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-sm font-medium text-muted-foreground">
            Applicants
          </h3>
          <RosterTable users={applicants} {...tableProps} />
        </section>
      )}

      <section className="space-y-2">
        <RosterTable users={members} {...tableProps} />
      </section>
    </div>
  );
}
